/**
 * Chat on screen at the table (UI prototype, Oct 31, 2026): David wants chat visible to players instead of behind the
 * "Chat & log" button. The hand log and score sheet stay in the slide-in panel ("Log & scores").
 *
 * Where it sits is the one thing the two prototype branches change:
 *   UI-Chat-Side   → CHAT_DOCK = 'side':   a narrow column on the right of the table;
 *   UI-Chat-Bottom → CHAT_DOCK = 'bottom': a strip along the bottom, under your cards;
 *   UI-Chat-Mix    → CHAT_DOCK = 'auto':   the side on wide screens, the bottom on phones and upright tablets.
 *
 * Built once and kept outside the table's redraw (like the side panel), so a half-typed message and the phone
 * keyboard aren't lost when a card is played. New lines are appended; the list follows the latest line unless the
 * player has scrolled up to read.
 */

import { type ChatEntry, EMOTES, LIMITS } from '@euchre/core';
import { h } from '../dom.js';
import { app, rerender, send } from '../state.js';
import { heckleText } from './voice.js';

export type ChatDockPlace = 'side' | 'bottom';
/** Which prototype this branch is: 'auto' picks by screen (David, Oct 31, 2026: "a mix of the two depending on device"). */
export const CHAT_DOCK: ChatDockPlace | 'auto' = 'auto';

/** Wide screens with room to spare (computers, iPads sideways): a column at the side. Phones, upright tablets: the bottom. */
const wideScreen = matchMedia('(min-width: 900px) and (min-height: 501px)');
wideScreen.addEventListener('change', () => rerender());

export function dockPlace(): ChatDockPlace {
  if (CHAT_DOCK !== 'auto') return CHAT_DOCK;
  return wideScreen.matches ? 'side' : 'bottom';
}

let root: HTMLElement | null = null;
let list: HTMLUListElement;
let emotePop: HTMLElement;
let emoteBtn: HTMLButtonElement;
let shown = new Set<number>();
let shownRoom = '';
let shownTalk = '';

function line(e: ChatEntry): HTMLElement {
  if (!e.from && e.tone === 'vegas') {
    return h('li', { class: 'dock-line heckle' }, h('span', { class: 'who dealer', text: 'Dealer' }), ' ', h('span', { text: heckleText(e, app.ui.dealerTalk) }));
  }
  if (!e.from) return h('li', { class: 'dock-line system', text: e.text });
  const mine = app.room && e.from.playerId === app.room.you.playerId;
  const team = e.from.seat === null ? 'watcher' : `team-${e.from.seat % 2}`;
  return h('li', { class: 'dock-line' }, h('span', { class: `who ${team}`, text: mine ? 'You' : e.from.name }), ' ', h('span', { text: e.text }));
}

function setEmotesOpen(open: boolean): void {
  emotePop.hidden = !open;
  emoteBtn.setAttribute('aria-expanded', String(open));
}

function build(): HTMLElement {
  list = h('ul', { class: 'dock-list', attrs: { 'aria-live': 'polite', 'aria-label': 'Table chat' } });
  const input = h('input', {
    id: 'dock-input',
    class: 'field dock-input',
    placeholder: 'Say something…',
    maxLength: LIMITS.chatMax,
    autocomplete: 'off',
    attrs: { 'aria-label': 'Say something to the table', enterkeyhint: 'send' },
  });
  emotePop = h(
    'div',
    { class: 'dock-emotes', attrs: { role: 'group', 'aria-label': 'Reactions', hidden: '' } },
    EMOTES.map((emote) =>
      h('button', {
        class: 'emote-btn',
        type: 'button',
        text: emote,
        title: `React ${emote}`,
        onclick: () => {
          send({ type: 'emote', emote });
          setEmotesOpen(false);
        },
      }),
    ),
  );
  emoteBtn = h('button', {
    class: 'btn small dock-emote-toggle',
    type: 'button',
    text: '🙂',
    title: 'Reactions',
    attrs: { 'aria-label': 'Reactions', 'aria-expanded': 'false', 'aria-haspopup': 'true' },
    onclick: () => setEmotesOpen(emotePop.hidden !== false),
  });
  const form = h(
    'form',
    {
      class: 'dock-form',
      onsubmit: (ev) => {
        ev.preventDefault();
        const text = input.value.trim();
        if (!text) return;
        send({ type: 'chat', text });
        input.value = '';
      },
    },
    emoteBtn,
    input,
    h('button', { class: 'btn small primary dock-send', type: 'submit', text: 'Send' }),
  );
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') {
      setEmotesOpen(false);
      input.blur();
    }
    ev.stopPropagation(); // typing never reaches the table's keyboard shortcuts
  });
  // Upright phones show the side chat as a slim strip of messages; this opens a typing bar along the bottom.
  const openBtn = h('button', {
    class: 'btn small dock-open',
    type: 'button',
    text: '💬 Chat',
    attrs: { 'aria-label': 'Write a chat message' },
    onclick: () => {
      root!.classList.add('typing');
      input.focus();
    },
  });
  const close = () => {
    root!.classList.remove('typing');
    setEmotesOpen(false);
  };
  form.addEventListener('submit', () => setTimeout(close, 0));
  input.addEventListener('blur', () => setTimeout(() => {
    if (!root!.contains(document.activeElement) && emotePop.hidden) close();
  }, 150));
  return h('aside', { class: 'chat-dock', attrs: { 'aria-label': 'Table chat' } }, list, openBtn, emotePop, form);
}

/** The chat box for the table screen (the same element every time). */
export function chatDock(): HTMLElement {
  root ??= build();
  const room = app.room;
  // A new table, or a change of dealer voice (Plain hides the dealer's heckles): start the list again.
  if (!room || shownRoom !== room.code || shownTalk !== app.ui.dealerTalk) {
    shownRoom = room?.code ?? '';
    shownTalk = app.ui.dealerTalk;
    shown = new Set();
    list.replaceChildren();
  }
  const atBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 30;
  for (const e of app.chat) {
    if (shown.has(e.id)) continue;
    shown.add(e.id);
    if (e.tone === 'vegas' && app.ui.dealerTalk === 'plain') continue;
    list.appendChild(line(e));
  }
  while (list.children.length > 80) list.firstElementChild?.remove();
  if (atBottom) list.scrollTop = list.scrollHeight;
  // It's on screen, so nothing is unread.
  app.unreadChat = 0;
  return root;
}

const uprightPhone = matchMedia('(max-width: 820px) and (orientation: portrait)');

/**
 * Side prototype on an upright phone: line the chat strip up with the middle of the felt (below the players along the
 * top, above your prompt), measured after each redraw, so it never covers a name plate or the prompt.
 */
export function placeDock(): void {
  if (!root) return;
  const centre = document.querySelector<HTMLElement>('.table-screen .centre');
  if (dockPlace() !== 'side' || !uprightPhone.matches || !centre || !root.isConnected) {
    root.style.top = '';
    root.style.bottom = '';
    return;
  }
  const r = centre.getBoundingClientRect();
  root.style.top = `${Math.round(r.top)}px`;
  root.style.bottom = `${Math.round(window.innerHeight - r.bottom)}px`;
}
addEventListener('resize', placeDock);
